import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

/**
 * A request rejected for its session (401, or API Gateway's 403 from the SSO authorizer) signs out:
 * AuthService.logout lets the SSO SDK decide between signing in again and ending a looping session.
 * Other 403s (no access to a page or a record) are left to the page.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || this.isGatewayDenied(error)) {
          // Resolved here, not in the constructor: AuthService needs HttpClient, which needs this interceptor.
          this.injector.get(AuthService).logout(true);
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * API Gateway's answer when its authorizer rejects the token, e.g. the SSO session behind it
   * ended (logout, switch back, idle timeout): a bare 403 {"message":"Forbidden"}, as no 401
   * can be returned from there. The services' own 403s carry other bodies.
   */
  private isGatewayDenied(error: HttpErrorResponse): boolean {
    const body = error.error;
    return error.status === 403
      && body !== null && typeof body === 'object'
      && Object.keys(body).length === 1 && body.message === 'Forbidden';
  }
}
